import { AccessLevel, Capability, Role } from '@/enums/auth'
import { useAppStore }   from '@/stores/app'
import { useFetchStore } from '@/stores/fetch'
import type { MeResponse } from '@/types/Session'

/**
 * The panel's own login entry point — **not** the IdP's. `/auth/login` is a server route that
 * mints PKCE + state + nonce, writes the four pre-auth cookies (§10.1) and 302s onwards; the
 * SPA has no OAuth role whatsoever (§9.6) and never learns a client id, an endpoint or a scope.
 */
const LOGIN_ENDPOINT = '/api/tpm/v1/auth/login'

/** Where a signed-in user with no better idea of where they were heading ends up. */
export const DEFAULT_REDIRECT = '/dashboard'

/**
 * A session flag hydrated by `/auth/me`, never a cookie or token read: the session is the
 * opaque `__Host-tpm_session` cookie the server set `HttpOnly`, and the SPA cannot see it (D76).
 * Callers that run before the bootstrap has settled must `await appStore.sessionReady` first
 * (the router guard does exactly that).
 */
export const isAuthenticated = (): boolean => useAppStore().isLoggedIn()

/** Signed in, but no role and no assignment — the `/no-access` case (§10.2). */
export const hasAnyAccess = (): boolean => useAppStore().hasAnyAccess()

/**
 * Kicks off the one `/auth/me` call the SPA makes at boot and parks the promise on the app
 * store as `sessionReady`. Idempotent: a second call returns the in-flight promise.
 * A 401 here is the ordinary signed-out path, not an error.
 */
export const loadSession = (): Promise<void> => {
    const appStore = useAppStore()
    if (appStore.sessionReady) return appStore.sessionReady

    const promise = (async () => {
        try {
            const fetchStore = useFetchStore()
            const { statusCode, data } = await fetchStore.fetch('/auth/me').get().json<MeResponse>()

            if (statusCode.value === 200 && data.value) appStore.setSession(data.value)
            else appStore.clearSession()
        } catch {
            // This promise is awaited by the router guard, and a guard that throws aborts the
            // navigation — leaving the browser wherever it was, which at boot is nowhere. So the
            // bootstrap never rejects: whatever went wrong, the honest reading of "I could not
            // establish a session" is signed-out, and signed-out has a destination (/login).
            appStore.clearSession()
        } finally {
            // The one place this flag is set. Everything that needs to know whether the bootstrap
            // is done asks the store, not `session.ready` — see the note there.
            appStore.markSessionSettled()
        }
    })()

    appStore.setSessionReady(promise)
    return promise
}

/**
 * The client half of W1.3's open-redirect guard: a redirect target is a **relative path and
 * nothing else**. The server validates the same thing on the way in and on the way out (§10.1)
 * — this copy exists because the value also travels through the SPA's own `?redirect=` query,
 * where a crafted link would otherwise turn our login button into an open redirector.
 *
 * Rejected: absolute URLs (`https://evil`), scheme-relative (`//evil`), and the backslash form
 * (`/\evil`) that browsers normalise into `//evil` — the classic bypass of a naive
 * "starts with a slash" check.
 */
export const safeRedirect = (value: unknown, fallback: string = DEFAULT_REDIRECT): string => {
    if (typeof value !== 'string' || value.length === 0) return fallback
    if (!value.startsWith('/')) return fallback
    if (value.startsWith('//') || value.startsWith('/\\')) return fallback
    return value
}

/**
 * Sign-in is a **full-page navigation**, never an API call, and it is **never automatic**: only
 * the Sign in button calls this (common contract §3), so a signed-out page sits still until the
 * person chooses. The panel answers with a 302 to the IdP and sets the four single-use
 * `__Host-tpm_oidc_*` pre-auth cookies on that top-level request (§10.1). Nothing comes back
 * through JavaScript — the session lands as the server-set `HttpOnly` `__Host-tpm_session`
 * cookie on the callback 302, and the SPA learns who it is from `/auth/me` alone. A live IT
 * Team ID SSO session may make the round trip password-less; that is the IdP's choice, not ours.
 */
export const startLogin = (redirect: string = DEFAULT_REDIRECT): void => {
    window.location.assign(`${LOGIN_ENDPOINT}?redirect=${encodeURIComponent(safeRedirect(redirect))}`)
}

/**
 * The value `/login?signed_out=` carries after a sign-out — what `Login.vue` turns into its
 * signed-out alert. `device` and `all` are the two confirmed outcomes; `unconfirmed` means the
 * cookie is gone from this browser but the server did not confirm the record was ended.
 */
export type SignedOutState = 'device' | 'all' | 'unconfirmed'

/**
 * Both sign-outs are **local to Traefik Proxy Manager** (handoff §7.1, D78). Neither one calls
 * the IdP, ends the IT Team ID SSO session, or touches any other application — so a Sign in
 * that follows may not ask for a password, and the signed-out page says so.
 *
 * `everywhere = false` → `POST /auth/logout`: the server permanently revokes **this** portal
 * session's record, then clears `__Host-tpm_session` on the response. A cookie that no longer
 * maps to a live record is a successful sign-out too (401 below).
 *
 * `everywhere = true` → `POST /auth/logout-everywhere`: the server bumps the caller's
 * `auth_epoch` and revokes every one of their TPM sessions on every device. Local-only, by the
 * same rule: "all my TPM sessions", never "all applications".
 *
 * Order matters and is asserted by `yarn check:session`:
 *
 *  1. `clearSession()` **first** — the non-secret identity leaves the store before the request
 *     goes out, so nothing renders as signed-in while the POST is in flight, whatever it answers.
 *  2. The POST, with the cookie the browser holds (`credentials: 'same-origin'`) and the
 *     `Origin`/`Sec-Fetch-Site` evidence the browser attaches to it — the server's CSRF check
 *     wants both, and a same-origin `fetch` supplies both without being asked. Only the **status**
 *     is read: a 204 has no body, so there is no `.json()` to parse. 204 and 401 (the cookie was
 *     already dead) are both "signed out"; anything else — 503 from a session store that could
 *     not confirm the revocation, 403 from a CSRF refusal, a network failure — is `unconfirmed`,
 *     and the page says so rather than claiming a revocation that did not happen.
 *  3. A **document load** rather than a `router.push`, because that is the only way to be
 *     certain no store, no poll and no cached page survives the sign-out. The Blank `/login`
 *     mounts nothing that polls.
 */
export const signOut = async (everywhere: boolean = false): Promise<void> => {
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()

    appStore.clearSession()

    let state: SignedOutState = 'unconfirmed'

    appStore.startLoading()
    try {
        const { statusCode } = await fetchStore.fetch(everywhere ? '/auth/logout-everywhere' : '/auth/logout').post()

        if (statusCode.value === 204 || statusCode.value === 401) state = everywhere ? 'all' : 'device'
    } catch {
        // A thrown fetch is a request that never reached the server: the cookie may still map to
        // a live record, which is exactly what `unconfirmed` tells the person.
    } finally {
        appStore.stopLoading()
    }

    window.location.assign(`/login?signed_out=${state}`)
}

const roles        = (): string[] => useAppStore().session.roles
const capabilities = (): string[] => useAppStore().session.capabilities

/**
 * UI gating, and **only** UI gating (§9.6): every one of these questions is answered again,
 * authoritatively, by `requireRole` / `requireInstanceAccess` on the server — including for
 * read-looking endpoints. Hiding a button here is a courtesy to the operator, never a control.
 *
 * Two rules keep this file from growing into a second authorization model:
 *
 *  1. **Role questions are answered from `roles`**, which `/auth/me` returns verbatim.
 *  2. **Instance questions are answered from the payload's `access_level`** (§6.1), which the
 *     server computed with `pkg/access`. `assignments` is carried in the session for the §8.4
 *     transparency screens (M10) and is deliberately *not* resolved here — the moment the SPA
 *     starts folding assignments into a level, §10.3's "the why screen can never disagree with
 *     what enforcement did" stops being true.
 */
export const can = {
    /** Full panel authority, including granting the `admin` role itself (§10.4). */
    admin: (): boolean => roles().includes(Role.Admin),

    /** `/users` and the access screens — admin or `user_manager` (§9.1, §10.4). */
    manageUsers: (): boolean => roles().includes(Role.Admin) || roles().includes(Role.UserManager),

    /**
     * Whether the caller may grant or revoke the `admin` role itself (§10.4) — the
     * `manage_roles` capability, admin only. The roles editor uses this, not `admin()` directly,
     * because the two questions read differently even though they resolve the same way today:
     * this one asks "may this admin-only control be touched", `admin()` asks "does this user hold
     * full panel authority". A `user_manager` sees every other role's checkbox editable and the
     * `admin` one visible-but-locked (D48's "admin-grant visibility rules") — never hidden, so a
     * target who already holds `admin` still shows it when the submitted role set is diffed.
     */
    manageRoles: (): boolean => capabilities().includes(Capability.ManageRoles),

    /** Fleet-wide read, by role alone — assignments can add instances but never remove them. */
    readAllInstances: (): boolean =>
        roles().includes(Role.Admin) ||
        roles().includes(Role.AllInstancesWrite) ||
        roles().includes(Role.AllInstancesRead),

    /** Fleet-wide write, including creating and deleting clients and instances (D45). */
    writeAllInstances: (): boolean =>
        roles().includes(Role.Admin) || roles().includes(Role.AllInstancesWrite),

    /**
     * "Is there at least one instance this user may change?" — the `write_instances` capability,
     * computed server-side from the same `WriteScope` every list query filters on.
     *
     * It is the gate for the **client-scoped** write actions (edit a client, create an instance
     * under one), and it is coarse on purpose. Those actions require write on a *client*, which
     * a client-scoped assignment grants and the SPA cannot resolve for itself: §10.3 forbids
     * folding `assignments` into an access level here, and instance payloads carry an
     * `access_level` while client payloads carry none. So the choice is between a coarse signal
     * that occasionally offers a button the server then refuses, and a role-only gate that hides
     * the button from exactly the client-scoped operator D45 designed the client scope for. The
     * first failure is a 403 toast; the second is a feature that does not exist for the person it
     * was built for.
     *
     * Fleet-wide operations — creating and deleting **clients** (D45) — keep using
     * `writeAllInstances` instead, because that genuinely is a role question.
     */
    writeSomewhere: (): boolean => capabilities().includes(Capability.WriteInstances),

    /** Raw role probe, for the rare screen that needs one this list has not named yet. */
    role: (role: Role | string): boolean => roles().includes(role),

    /** Raw capability probe over the `capabilities` array `/auth/me` returns. */
    capability: (capability: string): boolean => capabilities().includes(capability),

    /** Read on one instance, taken from that payload's server-computed `access_level`. */
    read: (level: AccessLevel | string | undefined): boolean =>
        level === AccessLevel.Read || level === AccessLevel.Write,

    /** Write on one instance, taken from that payload's server-computed `access_level`. */
    write: (level: AccessLevel | string | undefined): boolean => level === AccessLevel.Write,

    /** Anything at all — the difference between the app and the `/no-access` screen (§10.2). */
    anything: (): boolean => hasAnyAccess(),
}
